import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep, isEqual } from "lodash";
import { fetchAuthSession } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import {
  getMe,
  logout,
  selectUserAndToken,
} from "../../store/slices/authSlice";
import * as urls from "../../constants/urls";
import { useAppDispatch } from "../../store/hooks";

const ProtectedRoute = () => {
  const { user, accessToken } = useSelector(selectUserAndToken);
  const dispatch = useAppDispatch();
  const [currentSession, setCurrentSession] = useState<any>(null);

  const forceLogout = useCallback(() => {
    if (accessToken) {
      dispatch(getMe(accessToken));
    } else {
      dispatch(logout());
    }
  }, [accessToken, dispatch]);

  const handle = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!isEqual(session, currentSession)) {
        setCurrentSession(cloneDeep(session));
      }

      const tokens =
        await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();

      const timeout =
        (tokens?.accessToken?.payload?.exp ?? 0) * 1000 - new Date().getTime();
      if (timeout && timeout > 10 * 1000) {
        setTimeout(async () => {
          if (tokens?.refreshToken?.toString()) {
            // try to refresh token
            const newSession = await fetchAuthSession();
            setTimeout(() => setCurrentSession(cloneDeep(newSession)), 100);
          } else {
            forceLogout();
          }
          // dispatch logout and redirect to login
        }, Math.max(0, timeout - 30 * 1000));
      } else {
        forceLogout();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      forceLogout();
    }
  }, [currentSession, forceLogout]);

  useEffect(() => {
    handle();
  }, [handle]);

  return user ? <Outlet /> : <Navigate to={urls.LOGIN_PATH} replace />;
};

export default ProtectedRoute;
